import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  Contact,
  ImageIcon,
  Loader2,
  Save,
  Settings2,
  UploadCloud,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";

import establishmentsApi from "../../api/establishmentsApi";
import { useLanguage } from "../../contexts/LanguageContext";
import translateApiError from "../../utils/translateApiError";
import "../../styles/Establishments/establishment-form.css";

const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);

  return value === key
    ? fallback
    : value;
};

const DEFAULT_FORM = {
  business_id: "",
  owner_id: "",
  menu_template_id: "",
  name: "",
  slug: "",
  type: "restaurant",
  default_language: "fr",
  phone: "",
  whatsapp: "",
  email: "",
  address: "",
  description: "",
  is_active: true,
  ordering_enabled: false,
  allow_comments: true,
  allow_ratings: true,
  allow_likes: true,
  logo: null,
  cover: null,
};

const getRecord = (response) => {
  return (
    response?.data?.data ||
    response?.data ||
    null
  );
};

const createSlug = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const getFileName = (file) => {
  if (!file) {
    return "";
  }

  return file.name || "";
};

function EstablishmentFormSelect({
  name,
  value,
  options,
  onChange,
  label,
}) {
  const selectRef = useRef(null);

  const [open, setOpen] =
    useState(false);

  const selectedOption =
    options.find(
      (option) =>
        option.value === value,
    ) || options[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (
      event,
    ) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(
          event.target,
        )
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (
      event,
    ) => {
      if (
        event.key === "Escape"
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick,
    );

    document.addEventListener(
      "keydown",
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick,
      );

      document.removeEventListener(
        "keydown",
        handleEscape,
      );
    };
  }, [open]);

  return (
    <div
      ref={selectRef}
      className={`establishment-form-pretty-select ${
        open
          ? "establishment-form-pretty-select--open"
          : ""
      }`}
    >
      <input
        type="hidden"
        name={name}
        value={value}
      />

      <button
        type="button"
        className="establishment-form-pretty-select__button"
        onClick={() =>
          setOpen(
            (current) => !current,
          )
        }
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>
          {selectedOption?.label ||
            label}
        </span>

        <ChevronDown size={17} />
      </button>

      {open && (
        <div
          className="establishment-form-pretty-select__options"
          role="listbox"
        >
          {options.map(
            (option) => {
              const selected =
                option.value ===
                value;

              return (
                <button
                  key={
                    option.value
                  }
                  type="button"
                  className={`establishment-form-pretty-select__option ${
                    selected
                      ? "establishment-form-pretty-select__option--active"
                      : ""
                  }`}
                  onClick={() => {
                    setOpen(
                      false,
                    );

                    onChange(
                      option.value,
                    );
                  }}
                  role="option"
                  aria-selected={
                    selected
                  }
                >
                  <span>
                    {
                      option.label
                    }
                  </span>

                  {selected && (
                    <Check
                      size={15}
                    />
                  )}
                </button>
              );
            },
          )}
        </div>
      )}
    </div>
  );
}

function EstablishmentForm() {
  const navigate = useNavigate();

  const {
    establishmentId,
  } = useParams();

  const { t } =
    useLanguage();

  const isEditing =
    Boolean(establishmentId);

  const [form, setForm] =
    useState(DEFAULT_FORM);

  const [loading, setLoading] =
    useState(isEditing);

  const [saving, setSaving] =
    useState(false);

  const [message, setMessage] =
    useState(null);

  const [
    fieldErrors,
    setFieldErrors,
  ] = useState({});

  const [
    direction,
    setDirection,
  ] = useState(
    document.documentElement.dir ||
      "ltr",
  );

  const isRtl =
    direction === "rtl";

  // Keep the page synchronized with the global language direction.
  useEffect(() => {
    const updateDirection = () => {
      setDirection(
        document.documentElement.dir ||
          "ltr",
      );
    };

    updateDirection();

    const observer =
      new MutationObserver(
        updateDirection,
      );

    observer.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "dir",
          "lang",
        ],
      },
    );

    return () => {
      observer.disconnect();
    };
  }, []);

  const typeOptions =
    useMemo(
      () =>
        [
          "restaurant",
          "cafe",
          "snack",
          "bakery",
          "hotel",
          "riad",
          "other",
        ].map((option) => ({
          value: option,

          label:
            translateFallback(
              t,
              `establishments.types.${option}`,
              option,
            ),
        })),
      [t],
    );

  const languageOptions =
    useMemo(
      () =>
        [
          "fr",
          "ar",
          "en",
          "es",
        ].map((option) => ({
          value: option,

          label:
            translateFallback(
              t,
              `languages.${option}`,
              option,
            ),
        })),
      [t],
    );

  const pageTitle =
    isEditing
      ? translateFallback(
          t,
          "establishments.form.editTitle",
          "Edit establishment",
        )
      : translateFallback(
          t,
          "establishments.form.createTitle",
          "Add establishment",
        );

  const pageDescription =
    isEditing
      ? translateFallback(
          t,
          "establishments.form.editDescription",
          "Update establishment identity, contacts, manager and public settings.",
        )
      : translateFallback(
          t,
          "establishments.form.createDescription",
          "Create an establishment and connect it to a business and manager.",
        );

  const canSubmit =
    useMemo(() => {
      return (
        form.name.trim() &&
        form.business_id
      );
    }, [
      form.business_id,
      form.name,
    ]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const loadRecord =
      async () => {
        try {
          setLoading(true);

          const response =
            await establishmentsApi.getEstablishment(
              establishmentId,
            );

          const record =
            getRecord(
              response,
            );

          setForm({
            ...DEFAULT_FORM,

            business_id:
              record?.business_id ||
              record?.business?.id ||
              "",

            owner_id:
              record?.owner_id ||
              record?.owner?.id ||
              "",

            menu_template_id:
              record?.menu_template_id ||
              record?.menu_template
                ?.id ||
              "",

            name:
              record?.name ||
              "",

            slug:
              record?.slug ||
              "",

            type:
              record?.type ||
              "restaurant",

            default_language:
              record?.default_language ||
              record?.default_locale ||
              "fr",

            phone:
              record?.phone ||
              "",

            whatsapp:
              record?.whatsapp ||
              "",

            email:
              record?.email ||
              "",

            address:
              record?.address ||
              record?.adresse ||
              "",

            description:
              record?.description ||
              "",

            is_active:
              Boolean(
                record?.is_active,
              ),

            ordering_enabled:
              Boolean(
                record?.ordering_enabled,
              ),

            allow_comments:
              record?.allow_comments !==
              false,

            allow_ratings:
              record?.allow_ratings !==
              false,

            allow_likes:
              record?.allow_likes !==
              false,

            logo: null,
            cover: null,
          });
        } catch (error) {
          setMessage({
            variant: "error",

            text:
              translateApiError(
                error,
                t,
              ),
          });
        } finally {
          setLoading(false);
        }
      };

    loadRecord();
  }, [
    establishmentId,
    isEditing,
    t,
  ]);

  const updateField = (
    field,
    value,
  ) => {
    setForm((current) => ({
      ...current,

      [field]: value,

      ...(field === "name" &&
      !isEditing &&
      !current.slug
        ? {
            slug:
              createSlug(
                value,
              ),
          }
        : {}),
    }));

    setFieldErrors(
      (current) => ({
        ...current,
        [field]: "",
      }),
    );
  };

  const validate = () => {
    const errors = {};

    if (!form.business_id) {
      errors.business_id =
        translateFallback(
          t,
          "establishments.form.errors.businessRequired",
          "Business ID is required.",
        );
    }

    if (!form.name.trim()) {
      errors.name =
        translateFallback(
          t,
          "establishments.form.errors.nameRequired",
          "Name is required.",
        );
    }

    if (
      form.email &&
      !form.email.includes("@")
    ) {
      errors.email =
        translateFallback(
          t,
          "establishments.form.errors.emailInvalid",
          "Email address is not valid.",
        );
    }

    setFieldErrors(errors);

    const firstField =
      Object.keys(errors)[0];

    if (firstField) {
      const input =
        document.querySelector(
          `[name="${firstField}"]`,
        );

      input?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      window.setTimeout(
        () =>
          input?.focus(),
        250,
      );
    }

    return !firstField;
  };

  const handleSubmit =
    async (event) => {
      event.preventDefault();

      if (!validate()) {
        return;
      }

      try {
        setSaving(true);
        setMessage(null);

        const payload = {
          ...form,
          logo_path:
            undefined,
          cover_path:
            undefined,
        };

        if (isEditing) {
          // The public URL must remain stable when the name changes.
          delete payload.slug;
          await establishmentsApi.updateEstablishment(
            establishmentId,
            payload,
          );
        } else {
          await establishmentsApi.createEstablishment(
            payload,
          );
        }

        navigate(
          "/establishments",
          {
            state: {
              saved: true,
            },
          },
        );
      } catch (error) {
        setMessage({
          variant: "error",

          text:
            translateApiError(
              error,
              t,
            ),
        });
      } finally {
        setSaving(false);
      }
    };

  if (loading) {
    return (
      <main
        className={`establishment-form-page ${
          isRtl
            ? "establishment-form-page--rtl"
            : "establishment-form-page--ltr"
        }`}
        dir={direction}
      >
        <section className="establishment-form-loading-panel">
          <div className="establishment-form-loading">
            <Loader2
              size={30}
            />

            <span>
              {translateFallback(
                t,
                "establishments.form.loading",
                "Loading establishment...",
              )}
            </span>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      className={`establishment-form-page ${
        isRtl
          ? "establishment-form-page--rtl"
          : "establishment-form-page--ltr"
      }`}
      dir={direction}
    >
      <section className="establishment-form-header">
        <Link
          to="/establishments"
          className="establishment-form-back-button"
        >
          {isRtl ? (
            <ArrowRight
              size={18}
            />
          ) : (
            <ArrowLeft
              size={18}
            />
          )}

          <span>
            {translateFallback(
              t,
              "establishments.form.backToEstablishments",
              "Back to establishments",
            )}
          </span>
        </Link>

        <div className="establishment-form-header__copy">
          <p className="establishment-form-eyebrow">
            {translateFallback(
              t,
              "establishments.eyebrow",
              "Super admin",
            )}
          </p>

          <h1>
            {pageTitle}
          </h1>

          <span>
            {pageDescription}
          </span>
        </div>
      </section>

      {message && (
        <div
          className={`establishment-form-alert establishment-form-alert--${message.variant}`}
          role="alert"
        >
          {message.variant ===
          "success" ? (
            <CheckCircle2
              size={19}
            />
          ) : (
            <AlertCircle
              size={19}
            />
          )}

          <span>
            {message.text}
          </span>
        </div>
      )}

      <form
        className="establishment-form-shell"
        onSubmit={
          handleSubmit
        }
      >
        <section className="establishment-form-section">
          <div className="establishment-form-section__title">
            <span className="establishment-form-section__icon">
              <Building2
                size={22}
              />
            </span>

            <div>
              <h2>
                {translateFallback(
                  t,
                  "establishments.form.identity",
                  "Identity",
                )}
              </h2>

              <p>
                {translateFallback(
                  t,
                  "establishments.form.identityDescription",
                  "Basic information used across the dashboard and public menu.",
                )}
              </p>
            </div>
          </div>

          <div className="establishment-form-grid">
            <label
              className={
                fieldErrors.business_id
                  ? "error"
                  : ""
              }
            >
              <span>
                {translateFallback(
                  t,
                  "establishments.form.businessId",
                  "Business ID",
                )}
              </span>

              <input
                name="business_id"
                type="number"
                min="1"
                dir="ltr"
                value={
                  form.business_id
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    "business_id",
                    event.target
                      .value,
                  )
                }
              />

              {fieldErrors.business_id && (
                <small>
                  {
                    fieldErrors.business_id
                  }
                </small>
              )}
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  "establishments.form.ownerId",
                  "Manager / owner ID",
                )}
              </span>

              <input
                name="owner_id"
                type="number"
                min="1"
                dir="ltr"
                value={
                  form.owner_id
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    "owner_id",
                    event.target
                      .value,
                  )
                }
              />
            </label>

            <label
              className={
                fieldErrors.name
                  ? "error"
                  : ""
              }
            >
              <span>
                {translateFallback(
                  t,
                  "establishments.form.name",
                  "Name",
                )}
              </span>

              <input
                name="name"
                value={
                  form.name
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    "name",
                    event.target
                      .value,
                  )
                }
                placeholder={translateFallback(
                  t,
                  "establishments.form.namePlaceholder",
                  "Example: Maison Atlas",
                )}
              />

              {fieldErrors.name && (
                <small>
                  {
                    fieldErrors.name
                  }
                </small>
              )}
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  "establishments.form.slug",
                  "Public slug",
                )}
              </span>

              <input
                name="slug"
                dir="ltr"
                readOnly={isEditing}
                value={
                  form.slug
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    "slug",
                    createSlug(
                      event.target
                        .value,
                    ),
                  )
                }
                placeholder={translateFallback(
  t,
  "establishments.form.slugPlaceholder",
  "maison-atlas",
)}
              />
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  "establishments.form.type",
                  "Type",
                )}
              </span>

              <EstablishmentFormSelect
                name="type"
                value={
                  form.type
                }
                options={
                  typeOptions
                }
                onChange={(
                  value,
                ) =>
                  updateField(
                    "type",
                    value,
                  )
                }
                label={translateFallback(
                  t,
                  "establishments.form.type",
                  "Type",
                )}
              />
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  "establishments.form.defaultLanguage",
                  "Default language",
                )}
              </span>

              <EstablishmentFormSelect
                name="default_language"
                value={
                  form.default_language
                }
                options={
                  languageOptions
                }
                onChange={(
                  value,
                ) =>
                  updateField(
                    "default_language",
                    value,
                  )
                }
                label={translateFallback(
                  t,
                  "establishments.form.defaultLanguage",
                  "Default language",
                )}
              />
            </label>
          </div>
        </section>

        <section className="establishment-form-section">
          <div className="establishment-form-section__title">
            <span className="establishment-form-section__icon">
              <Contact
                size={22}
              />
            </span>

            <div>
              <h2>
                {translateFallback(
                  t,
                  "establishments.form.contact",
                  "Contact",
                )}
              </h2>

              <p>
                {translateFallback(
                  t,
                  "establishments.form.contactDescription",
                  "Public contact details shown to customers when needed.",
                )}
              </p>
            </div>
          </div>

          <div className="establishment-form-grid">
            <label>
              <span>
                {translateFallback(
                  t,
                  "establishments.form.phone",
                  "Phone",
                )}
              </span>

              <input
                name="phone"
                dir="ltr"
                value={
                  form.phone
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    "phone",
                    event.target
                      .value,
                  )
                }
              />
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  "establishments.form.whatsapp",
                  "WhatsApp",
                )}
              </span>

              <input
                name="whatsapp"
                dir="ltr"
                value={
                  form.whatsapp
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    "whatsapp",
                    event.target
                      .value,
                  )
                }
              />
            </label>

            <label
              className={
                fieldErrors.email
                  ? "error"
                  : ""
              }
            >
              <span>
                {translateFallback(
                  t,
                  "establishments.form.email",
                  "Email",
                )}
              </span>

              <input
                name="email"
                type="email"
                dir="ltr"
                value={
                  form.email
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    "email",
                    event.target
                      .value,
                  )
                }
              />

              {fieldErrors.email && (
                <small>
                  {
                    fieldErrors.email
                  }
                </small>
              )}
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  "establishments.form.menuTemplateId",
                  "Menu template ID",
                )}
              </span>

              <input
                name="menu_template_id"
                type="number"
                min="1"
                dir="ltr"
                value={
                  form.menu_template_id
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    "menu_template_id",
                    event.target
                      .value,
                  )
                }
              />
            </label>

            <label className="establishment-form-grid__full">
              <span>
                {translateFallback(
                  t,
                  "establishments.form.address",
                  "Address",
                )}
              </span>

              <textarea
                name="address"
                rows="3"
                value={
                  form.address
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    "address",
                    event.target
                      .value,
                  )
                }
              />
            </label>

            <label className="establishment-form-grid__full">
              <span>
                {translateFallback(
                  t,
                  "establishments.form.description",
                  "Description",
                )}
              </span>

              <textarea
                name="description"
                rows="4"
                value={
                  form.description
                }
                onChange={(
                  event,
                ) =>
                  updateField(
                    "description",
                    event.target
                      .value,
                  )
                }
              />
            </label>
          </div>
        </section>

        <section className="establishment-form-section">
          <div className="establishment-form-section__title">
            <span className="establishment-form-section__icon">
              <ImageIcon
                size={22}
              />
            </span>

            <div>
              <h2>
                {translateFallback(
                  t,
                  "establishments.form.media",
                  "Media",
                )}
              </h2>

              <p>
                {translateFallback(
                  t,
                  "establishments.form.mediaDescription",
                  "Upload the logo and cover image used by the public menu.",
                )}
              </p>
            </div>
          </div>

          <div className="establishment-form-upload-grid">
            <label className="establishment-form-upload">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(
                  event,
                ) =>
                  updateField(
                    "logo",
                    event.target
                      .files?.[0] ||
                      null,
                  )
                }
              />

              <span className="establishment-form-upload__preview">
                <UploadCloud
                  size={30}
                />
              </span>

              <strong>
                {translateFallback(
                  t,
                  "establishments.form.logo",
                  "Logo",
                )}
              </strong>

              <small>
                {getFileName(
                  form.logo,
                ) ||
                  translateFallback(
                    t,
                    "establishments.form.logoHint",
                    "PNG, JPG or WEBP up to 4 MB.",
                  )}
              </small>
            </label>

            <label className="establishment-form-upload">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(
                  event,
                ) =>
                  updateField(
                    "cover",
                    event.target
                      .files?.[0] ||
                      null,
                  )
                }
              />

              <span className="establishment-form-upload__preview">
                <UploadCloud
                  size={30}
                />
              </span>

              <strong>
                {translateFallback(
                  t,
                  "establishments.form.cover",
                  "Cover image",
                )}
              </strong>

              <small>
                {getFileName(
                  form.cover,
                ) ||
                  translateFallback(
                    t,
                    "establishments.form.coverHint",
                    "Large image for the public menu header.",
                  )}
              </small>
            </label>
          </div>
        </section>

        <section className="establishment-form-section">
          <div className="establishment-form-section__title">
            <span className="establishment-form-section__icon">
              <Settings2
                size={22}
              />
            </span>

            <div>
              <h2>
                {translateFallback(
                  t,
                  "establishments.form.settings",
                  "Settings",
                )}
              </h2>

              <p>
                {translateFallback(
                  t,
                  "establishments.form.settingsDescription",
                  "Control menu visibility, ordering and customer interactions.",
                )}
              </p>
            </div>
          </div>

          <div className="establishment-form-switch-grid">
            {[
              [
                "is_active",
                "active",
              ],
              [
                "ordering_enabled",
                "ordering",
              ],
              [
                "allow_comments",
                "comments",
              ],
              [
                "allow_ratings",
                "ratings",
              ],
              [
                "allow_likes",
                "likes",
              ],
            ].map(
              ([
                field,
                label,
              ]) => (
                <label
                  key={field}
                  className={`establishment-form-switch ${
                    form[field]
                      ? "active"
                      : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(
                      form[field],
                    )}
                    onChange={(
                      event,
                    ) =>
                      updateField(
                        field,
                        event.target
                          .checked,
                      )
                    }
                  />

                  <span className="establishment-form-switch__control">
                    <span />
                  </span>

                  <strong>
                    {translateFallback(
                      t,
                      `establishments.form.switches.${label}`,
                      label,
                    )}
                  </strong>
                </label>
              ),
            )}
          </div>
        </section>

        <div className="establishment-form-actions">
          <Link
            to="/establishments"
            className="establishment-form-cancel-button"
          >
            {translateFallback(
              t,
              "common.cancel",
              "Cancel",
            )}
          </Link>

          <button
            type="submit"
            className="establishment-form-save-button"
            disabled={
              saving ||
              !canSubmit
            }
          >
            {saving ? (
              <Loader2
                size={18}
              />
            ) : (
              <Save
                size={18}
              />
            )}

            <span>
              {isEditing
                ? translateFallback(
                    t,
                    "establishments.form.update",
                    "Update establishment",
                  )
                : translateFallback(
                    t,
                    "establishments.form.save",
                    "Create establishment",
                  )}
            </span>
          </button>
        </div>
      </form>
    </main>
  );
}

export default EstablishmentForm;